/**
 * client.ts — the shared gateway fetch client.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign).
 *
 * WHY one client: every call goes through the GPS Clinics gateway at
 * `GATEWAY_URL` (same-origin `/api` by default). This wrapper adds the
 * `Authorization: Bearer <token>` header on authed calls, handles a 401 by
 * clearing the session and notifying the AuthProvider (which routes to /auth),
 * and throws a typed {@link ApiError} carrying the HTTP status so every screen
 * can render a retryable error state (TA-06).
 *
 * PHI (R19): error messages carry status + path only — never a response body —
 * because bodies may contain PHI. The ONE exception is `parseError`, used only
 * for identity validation endpoints whose 400 bodies are Zod field errors
 * (field names + generic messages, never the submitted values).
 */
import { GATEWAY_URL } from '../config';
import { getToken, notifyUnauthorized } from '../auth/session';

/** Zod-style validation error body from the identity service. */
export interface FieldErrorBody {
  formErrors?: string[];
  fieldErrors?: Record<string, string[]>;
}

/** A failed gateway call. `status` drives retry/erorr UI; `fields` is auth-only. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly fields?: FieldErrorBody,
    /**
     * A human-readable validation detail parsed from a 400/422 body (Nest's
     * `{message}` — e.g. the D6 pack-maths gate on the prices admin surface).
     * PHI-free by construction: these bodies are field names + generic config
     * messages, never submitted values.
     */
    public readonly detail?: string,
    /**
     * A closed machine reason read out of the failed response's body by the
     * CALLER's own reader (`RequestOptions.readErrorReason`), e.g. the SCR read's
     * `'not_completed'` (2026-09-25). Only the reader's answer is kept, never the
     * body: the reader returns a value from its own closed vocabulary or
     * undefined, so nothing a service put in the body reaches this object
     * unchecked.
     */
    public readonly reason?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Attach the bearer token; a 401 clears the session. */
  auth?: boolean;
  /** Parse a 400/409/422/429 body into `ApiError.fields`/`detail` (validation + PHI-free conflict/limit messages only). */
  parseError?: boolean;
  /**
   * Send THIS bearer instead of the shared patient/staff session, and leave that
   * session untouched on a 401 (pharmacy dispensing lane, 2026-07-29).
   *
   * WHY a second seam rather than one token store: the pharmacy console signs in
   * through a different front door (`/auth-pharmacy` — an emailed link token
   * plus a personal code, no password) and holds its own session with its own
   * expiry (see pharmacy/session.ts). Sharing `gps.session` would let a
   * pharmacy principal satisfy `RequireRole`, and would let a pharmacy 401
   * bounce a signed-in patient in another tab to /auth. The caller owns the
   * 401 for these calls — see api/pharmacy.ts.
   */
  bearer?: string;
  /** Extra request headers (e.g. guest checkout `x-checkout-token`). */
  headers?: Record<string, string>;
  /**
   * Read a closed reason out of a FAILED response's JSON body, for a call whose
   * refusals are answers rather than faults (2026-09-25, the SCR read's 409
   * `{code:'account_not_completed', accountStatus}`).
   *
   * WHY A PER-CALL READER and not a generic "keep the body": bodies may carry
   * PHI (see the R19 note at the top of this file), so the client never keeps
   * one. The reader belongs to the endpoint's module, holds that endpoint's
   * strict wire shape, and returns only a value from its own closed vocabulary
   * (or undefined), which lands on `ApiError.reason`. It runs for any non-2xx
   * status, and a body that is not JSON, or a reader that throws, gives
   * undefined.
   */
  readErrorReason?: (body: unknown) => string | undefined;
}

/** Perform a gateway request, returning the parsed JSON (or undefined on 204). */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const {
    method = 'GET',
    body,
    auth = false,
    parseError = false,
    bearer,
    headers: extraHeaders,
    readErrorReason,
  } = options;
  const headers: Record<string, string> = { ...(extraHeaders ?? {}) };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (bearer) {
    headers.Authorization = `Bearer ${bearer}`;
  } else if (auth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${GATEWAY_URL}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  // An explicit `bearer` belongs to another session store, so a 401 on it must
  // NOT clear the patient/staff session or route that user to /auth — its owner
  // handles it (api/pharmacy.ts).
  if (response.status === 401 && auth && !bearer) {
    // The session is dead — clear it and let the app route to /auth.
    notifyUnauthorized();
  }

  if (!response.ok) {
    let fields: FieldErrorBody | undefined;
    let detail: string | undefined;
    let reason: string | undefined;
    // Parse PHI-free Nest/Zod bodies for validation + auth failures the UI maps:
    // 400/422 field errors, 409 conflicts, 403 age-gate copy, 404/502 ops
    // failures, and 429 send caps.
    //
    // 429 joined the list for the plan-link resend cap (clinic-service,
    // 2026-09-04): its body is the whole point of the response — "this plan
    // link has already been sent 10 times in the last 24 hours — check with the
    // patient before sending again" is the sentence a back-office desk needs,
    // and it is PHI-free by construction (a count and a window, no address, no
    // name). Without this the UI could only say "something went wrong" and the
    // desk would keep clicking.
    const liftFields =
      parseError &&
      (response.status === 400 ||
        response.status === 403 ||
        response.status === 404 ||
        response.status === 409 ||
        response.status === 422 ||
        response.status === 429 ||
        response.status === 502 ||
        response.status === 503);
    // The body is read ONCE (a Response body can only be consumed once), then
    // offered to the field lift and to the caller's reason reader.
    if (liftFields || readErrorReason) {
      let parsedBody: unknown;
      try {
        parsedBody = await response.json();
      } catch {
        parsedBody = undefined;
      }
      if (liftFields && parsedBody !== null && typeof parsedBody === 'object') {
        const parsed = parsedBody as FieldErrorBody & {
          message?: string | string[] | { message?: string };
        };
        if (parsed.fieldErrors || parsed.formErrors) fields = parsed;
        // Nest's exception body is `{message, error, statusCode}` — a string,
        // string[] (Zod pipe), or object (age-gate ForbiddenException payload).
        const raw = parsed.message;
        if (typeof raw === 'string') detail = raw;
        else if (Array.isArray(raw)) detail = raw.join(' ');
        else if (raw && typeof raw === 'object' && typeof raw.message === 'string') detail = raw.message;
      }
      if (readErrorReason && parsedBody !== undefined) {
        try {
          reason = readErrorReason(parsedBody);
        } catch {
          reason = undefined;
        }
      }
    }
    throw new ApiError(response.status, `Gateway request failed (${response.status}) on ${path}`, fields, detail, reason);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** GET helper. */
export function get<T>(path: string, auth = false): Promise<T> {
  return request<T>(path, { method: 'GET', auth });
}

/** POST helper. */
export function post<T>(path: string, body: unknown, opts: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
  return request<T>(path, { method: 'POST', body, ...opts });
}

/** PATCH helper. */
export function patch<T>(path: string, body: unknown, auth = true): Promise<T> {
  return request<T>(path, { method: 'PATCH', body, auth });
}

/** PUT helper (idempotent upsert — e.g. admin compensation rates). */
export function put<T>(path: string, body: unknown, auth = true): Promise<T> {
  return request<T>(path, { method: 'PUT', body, auth });
}

/** DELETE helper (admin invite-revoke / user-delete — always authed). */
export function del<T>(path: string, auth = true): Promise<T> {
  return request<T>(path, { method: 'DELETE', auth });
}

/**
 * Build a `?a=1&b=x` query string, skipping undefined/empty values; '' when
 * nothing survives. Values are URL-encoded via URLSearchParams. Shared by the
 * three per-service audit reads (G2) so limit/before serialise identically.
 * An ARRAY value joins with commas (the services' `?action=a,b` comma-list
 * convention, UX Wave 3c); an empty array is skipped like an empty string.
 */
export function queryString(params: object): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params) as Array<[string, string | number | string[] | undefined]>) {
    if (value === undefined || value === '') continue;
    if (Array.isArray(value)) {
      if (value.length > 0) query.set(key, value.join(','));
      continue;
    }
    query.set(key, String(value));
  }
  const built = query.toString();
  return built ? `?${built}` : '';
}
